//@flow
import pubSub from 'pubsub-js';

import type {
    EnvelopeType,
    MessageType,
    DataType,
    SubscriptionType,
} from './types';

type StickyTopicsType = Set<string>;
type TopicToEnvelopesMapType = Map<string, Array<EnvelopeType>>;
// FIXME: Describing a key of the WeakMap to be an Object instead of EnvelopeType because of flowtype problem.
type EnvelopeToConsumptionsMapType = WeakMap<Object, number>;
type TopicToSubscriptionsCountMapType = Map<string, number>;
type TokenToTopicMapType = Map<string, string>;

// Multiple instances of message bus can be used in applications like sportsbook.
// Due to this reason, it is required to synchronize those different instances and the only
// synchronization point available at the moment is shared dependency which is `pubsub-js`.
// Not doing the synchronization we're about to lose sticky messages between instances as well as envelopes.
// IMPORTANT: If you're about to change anything here, please take extra care to analyze and test it first!
const pubSubSharedKey = '@@psmb@@';
if (!Object.prototype.hasOwnProperty.call(pubSub, pubSubSharedKey)) {
    Object.defineProperty(pubSub, pubSubSharedKey, {
        value: {
            stickyTopics: new Set(),
            topicToEnvelopesMap: new Map(),
            envelopeToConsumptionsMap: new WeakMap(),
            topicToSubscriptionsCountMap: new Map(),
            tokenToTopicMap: new Map(),
        },
        enumerable: false,
        configurable: false,
    });
}
const stickyTopics: StickyTopicsType = pubSub[pubSubSharedKey].stickyTopics;
const topicToEnvelopesMap: TopicToEnvelopesMapType = pubSub[pubSubSharedKey].topicToEnvelopesMap;
const envelopeToConsumptionsMap: EnvelopeToConsumptionsMapType = pubSub[pubSubSharedKey].envelopeToConsumptionsMap;
const topicToSubscriptionsCountMap: TopicToSubscriptionsCountMapType =
    pubSub[pubSubSharedKey].topicToSubscriptionsCountMap;
const tokenToTopicMap: TokenToTopicMapType = pubSub[pubSubSharedKey].tokenToTopicMap;

// eslint-disable-next-line flowtype/no-weak-types
const defaultTo = (value: any, fallback: any) => (value === undefined ? fallback : value);

const isTopicSticky = (topic: string) => stickyTopics.has(topic);
const markTopicAsSticky = (topic: string) => {
    stickyTopics.add(topic);
};
const upsertEnvelope = (envelope: EnvelopeType) => {
    const { topic } = envelope;
    let envelopes = defaultTo(topicToEnvelopesMap.get(topic), []);
    envelopes = isTopicSticky(topic) ? [envelope] : [...envelopes, envelope];
    topicToEnvelopesMap.set(topic, envelopes);
    envelopeToConsumptionsMap.set(envelope, 0);
};
const getEnvelopeByTopic = (topic: string): ?EnvelopeType => {
    const envelopes = topicToEnvelopesMap.get(topic);
    return envelopes && envelopes[0];
};
const shiftEnvelopeByTopic = (topic: string) => {
    const envelopes = defaultTo(topicToEnvelopesMap.get(topic), []);
    topicToEnvelopesMap.set(topic, envelopes.slice(1));
};
const increaseConsumptionsForTopic = (topic: string): number => {
    const envelope = defaultTo(getEnvelopeByTopic(topic), {});
    const consumptions = envelopeToConsumptionsMap.get(envelope);
    const nevValue = consumptions + 1;
    envelopeToConsumptionsMap.set(envelope, nevValue);
    return nevValue;
};
const getSubscriptionsCountByTopic = (topic: string): number => defaultTo(topicToSubscriptionsCountMap.get(topic), 0);
const storeTopicForToken = (token: string, topic: string) => {
    tokenToTopicMap.set(token, topic);
};
const increaseSubscriptionsCountForTopic = (topic: string) => {
    const subscriptionsCount = getSubscriptionsCountByTopic(topic);
    topicToSubscriptionsCountMap.set(topic, subscriptionsCount + 1);
};
const decreaseSubscriptionsCountForSubscriptionToken = (token: string) => {
    const topic = defaultTo(tokenToTopicMap.get(token), '');
    const subscriptionsCount = defaultTo(topicToSubscriptionsCountMap.get(topic), 0);
    topicToSubscriptionsCountMap.set(topic, subscriptionsCount - 1);
};

//eslint-disable-next-line  no-confusing-arrow
const toMessage = (topicOrMessage: string | MessageType, data?: DataType): MessageType =>
    (typeof topicOrMessage === 'string')
        ? {
            topic: topicOrMessage,
            headers: {},
            data,
        }
        : {
            topic: topicOrMessage.topic,
            headers: defaultTo(topicOrMessage.headers, {}),
            data: defaultTo(topicOrMessage.data, null),
        };

const toEnvelope = (message: MessageType): EnvelopeType => ({
    topic: String(message.topic),
    headers: defaultTo(message.headers, {}),
    data: message.data,
    timestamp: Date.now(),
});

/**
 * Subscribe to specific topic on the message bus.
 * In case the targeted topic has already been published in a sticky way,
 * once the subscription is confirmed by returning a subscription token,
 * the given subscriber will be notified with data last used for specified topic name.
 * @param {string|SubscriptionType} topicOrSubscription name of the subscription topic
 * @param {Function} handler subscriber handler that will be called when a given topic is published
 * @returns {string} token unique identifier of the subscription that can be used to unsubscribe
 */
// eslint-disable-next-line  no-confusing-arrow
export const subscribe = (topicOrSubscription: string | SubscriptionType, handler: Function = () => {}): string => {
    let topic;
    let callback;
    if (typeof topicOrSubscription === 'string') {
        topic = topicOrSubscription;
        callback = handler;
    } else {
        const subscription: SubscriptionType = topicOrSubscription;
        topic = subscription.topic;
        // eslint-disable-next-line complexity
        callback = (_topic: string, _data: ?DataType) => {
            let envelope: ?EnvelopeType = getEnvelopeByTopic(subscription.topic);
            if (envelope === undefined || envelope === null) {
                envelope = toEnvelope(toMessage(_topic, _data));
                envelope.timestamp = 0;
                if (process.env.NODE_ENV !== 'production') {
                    console.warn(
                        `Message bus received an incompatible message on topic "${_topic}".\n` +
                        'It means that the message has been published by old `@wh/message-bus@1.2` instance.\n' +
                        'Due to this reason, the `timestamp` field has been lost.',
                    );
                }
            }
            subscription.callback({
                ...envelope,
                data: defaultTo(envelope.data, null),
            });
        };
    }

    increaseSubscriptionsCountForTopic(topic);

    // eslint-disable-next-line flowtype/require-parameter-type
    const token = pubSub.subscribe(topic, (...args) => {
        callback(...args);
        if (!isTopicSticky(topic)) {
            const consumptions = increaseConsumptionsForTopic(topic);
            if (consumptions >= getSubscriptionsCountByTopic(topic)) {
                shiftEnvelopeByTopic(topic);
            }
        }
    });
    if (isTopicSticky(topic)) {
        setTimeout(() => {
            callback(topic, defaultTo(getEnvelopeByTopic(topic), {}).data);
        }, 0);
    }

    storeTopicForToken(token, topic);
    return token;
};

export const unsubscribe = (value: string) => {
    decreaseSubscriptionsCountForSubscriptionToken(value);
    return pubSub.unsubscribe(value);
};

/**
 * Publish specific topic on the message bus.
 * @param {string|MessageType} topicOrMessage Name of the subscription topic
 * @param {DataType} data Payload to be carried with the published topic
 * @returns {Boolean} true If any handlers for a given topic found, false in case no action done
 */
//eslint-disable-next-line  no-confusing-arrow
export const publish = (topicOrMessage: string | MessageType, data?: DataType): boolean => {
    const envelope = toEnvelope(toMessage(topicOrMessage, data));
    const { topic } = envelope;
    if (getSubscriptionsCountByTopic(topic) > 0 || isTopicSticky(topic)) {
        upsertEnvelope(envelope);
    }
    return pubSub.publish(envelope.topic, envelope.data);
};

/**
 * Publish specific topic on the message bus in a sticky way. It means that the last published data will be
 * remembered internally for any subsequent republishing.
 * @param {string} topic name of the subscription topic
 * @param {DataType} data payload to be carried with the published topic
 * @returns {Boolean} true if any handlers for a given topic found, false in case no action done
 */
export const publishSticky = (topic: string, data?: DataType): boolean => {
    // Beware!
    // `publishSticky` does not support new API, so that
    // the topic has to be explicitly converted to string.
    markTopicAsSticky(String(topic));
    return publish(String(topic), data);
};
